/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
    type AxiosRequestConfig,
    type AxiosInterceptorManager,
    type AxiosResponse,
    type AxiosError,
} from 'axios';
import { castArray, devError } from '../utils';

/**
 * Enhanced request config for API calls.
 */
export interface ApiAxiosRequestConfig extends AxiosRequestConfig {
    readonly $id: string;
    readonly $isRaw?: true;
}

/**
 * Response of API calls.
 */
export interface ApiAxiosResponse<T = any> extends AxiosResponse<T> {
    readonly config: ApiAxiosRequestConfig;
}

/**
 * Error of API calls.
 */
export interface ApiAxiosError<T = any> extends AxiosError<T> {
    readonly config: ApiAxiosRequestConfig;
}

/**
 * Checks whether the given error is an error from an API call.
 * @param e The error.
 */
export function isApiError(e: any): e is ApiAxiosError {
    return e?.isAxiosError && e.isApiError;
}

type NoDataMethod = 'get' | 'delete' | 'head' | 'options';
type DataMethod = 'post' | 'put' | 'patch';

interface ApiAxiosInstance {
    readonly defaults: AxiosRequestConfig;
    readonly interceptors: {
        request: AxiosInterceptorManager<ApiAxiosRequestConfig>;
        response: AxiosInterceptorManager<ApiAxiosResponse>;
    };
    getUri(config?: AxiosRequestConfig): string;
    raw<T = any>(config: AxiosRequestConfig): Promise<ApiAxiosResponse<T>>;
    request<T = any>(config: AxiosRequestConfig): Promise<T>;
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    options<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const isoDateRx =
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|(?:[+-]\d{2}(?::?\d{2})?))?)?$/;
function jsonReviver(key: string, value: any): any {
    if (typeof value === 'string' && isoDateRx.test(value)) {
        return new Date(value);
    }
    return value;
}

function processError(e: any): any {
    if (e?.isAxiosError) {
        e.isApiError = true;
        const err = e as AxiosError;
        if (err.response?.data) {
            err.message = err.response.data.message || err.response.data.error || err.message;
        }
    }
    return Promise.reject(e);
}

/**
 * Creates an preconfigured interface for Ajax requests.
 * @param id The ID of the api.
 * @param defaults Default parameters.
 */
export function createApi(id: string, defaults?: AxiosRequestConfig): ApiAxiosInstance {
    const api: ApiAxiosInstance = axios.create(defaults) as any;

    api.interceptors.response.use(undefined, processError);

    // raw method that works like the original request method
    api.raw = config => {
        return api.request({
            ...(config || {}),
            $isRaw: true,
        } as ApiAxiosRequestConfig);
    };

    ((orig: (config: AxiosRequestConfig) => Promise<ApiAxiosResponse>) => {
        api.request = function (config) {
            config = config || {};
            const transformResponse = config.transformResponse
                ? castArray(config.transformResponse)
                : [];
            return orig
                .call(this, {
                    ...config,
                    $id: id,
                    transformResponse: [
                        data => {
                            if (typeof data === 'string') {
                                try {
                                    data = JSON.parse(data, jsonReviver);
                                } catch (e) {
                                    devError(e as Error);
                                }
                            }
                            return data;
                        },
                        ...transformResponse,
                    ],
                } as ApiAxiosRequestConfig)
                .then(r => (r.config.$isRaw === true ? r : r.data));
        };
    })(api.request);

    (['get', 'delete', 'head', 'options'] as NoDataMethod[]).forEach(
        method =>
            (api[method] = (url, config) => {
                return api.request({
                    ...(config || {}),
                    url,
                    method,
                });
            }),
    );

    (['post', 'put', 'patch'] as DataMethod[]).forEach(
        method =>
            (api[method] = (url, data, config) => {
                return api.request({
                    ...(config || {}),
                    url,
                    method,
                    data,
                });
            }),
    );

    return api;
}
